import { Button, Card, Input, Spin, Switch, Tooltip, Tree } from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import type { GlobalToken } from 'antd/es/theme/interface';
import { DownOutlined, EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import type { Key } from 'react';
import type { Location } from '../types';

const { Search } = Input;

type TreeDropInfo = Parameters<NonNullable<TreeProps<DataNode>['onDrop']>>[0];
type AllowDropOptions = Parameters<NonNullable<TreeProps<DataNode>['allowDrop']>>[0];
type DraggableConfig = Exclude<NonNullable<TreeProps<DataNode>['draggable']>, boolean | ((node: DataNode) => boolean)>;

export type LocationsTreePanelProps = {
  isLoading: boolean;
  selectedBranchId: string | null;
  locationsTree: Location[] | undefined;
  rootLocationId: string | null;
  rootLocation: Location | null;
  treeData: DataNode[];
  expandedKeys: string[];
  onExpand: (keys: string[]) => void;
  selectedTreeKeys: string[];
  onSelect: (selectedKeys: Key[]) => void;
  canManage: boolean;
  isHierarchyMoveEnabled: boolean;
  onHierarchyMoveEnabledChange: (checked: boolean) => void;
  onHierarchyDrop: (info: TreeDropInfo) => void;
  isLocationDescendant: (dragId: string, dropId: string) => boolean;
  token: GlobalToken;
  onReload: () => void;
};

export function LocationsTreePanel({
  isLoading,
  selectedBranchId,
  locationsTree,
  rootLocationId,
  rootLocation,
  treeData,
  expandedKeys,
  onExpand,
  selectedTreeKeys,
  onSelect,
  canManage,
  isHierarchyMoveEnabled,
  onHierarchyMoveEnabledChange,
  onHierarchyDrop,
  isLocationDescendant,
  token,
  onReload,
}: LocationsTreePanelProps) {
  const draggableConfig: DraggableConfig = {
    icon: false,
    nodeDraggable: (node) => String(node.key ?? '') !== rootLocationId,
  };

  const allowDrop: TreeProps<DataNode>['allowDrop'] = (options: AllowDropOptions) => {
    if (!canManage || !isHierarchyMoveEnabled) return false;
    const dragId = String(options.dragNode?.key ?? '');
    const dropId = String(options.dropNode?.key ?? '');
    if (!dragId || !dropId) return false;
    if (!rootLocationId) return false;
    if (dragId === rootLocationId) return false;
    if (dragId === dropId) return false;
    if (isLocationDescendant(dragId, dropId)) return false;

    // Root node: allow drop *into* root only (no gap drop).
    if (dropId === rootLocationId) {
      return Number(options.dropPosition) === 0;
    }

    return true;
  };

  return (
    <Card
      title="階層"
      styles={{ body: { padding: '12px 8px' } }}
      extra={(
        <Tooltip title="データを更新">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={onReload}
            loading={isLoading}
          />
        </Tooltip>
      )}
    >
      {!selectedBranchId ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
          <EnvironmentOutlined style={{ fontSize: 32, marginBottom: 8 }} />
          <div>拠点を選択してください</div>
        </div>
      ) : isLoading || locationsTree === undefined ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
          <Spin />
        </div>
      ) : !rootLocationId ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
          <EnvironmentOutlined style={{ fontSize: 32, marginBottom: 8 }} />
          <div>ルートエリアが見つかりません</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            データ変換（ルート正規化）後に再読み込みしてください
          </div>
        </div>
      ) : (
        <>
          <Search
            placeholder="エリアを検索"
            size="small"
            style={{ marginBottom: 12 }}
          />
          <div className="location-tree-panel">
            <Tree
              showLine
              switcherIcon={<DownOutlined />}
              draggable={
                canManage && isHierarchyMoveEnabled
                  ? draggableConfig
                  : false
              }
              allowDrop={allowDrop}
              onDrop={onHierarchyDrop}
              expandedKeys={expandedKeys}
              onExpand={(keys) => onExpand(keys as string[])}
              selectedKeys={selectedTreeKeys}
              onSelect={(keys) => onSelect(keys)}
              treeData={treeData}
            />
          </div>
          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <Tooltip title="ONにすると、ドラッグ＆ドロップでエリアの階層移動・並び替えができます（この設定は保存されません）">
              <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                階層移動
              </span>
            </Tooltip>
            <Switch
              size="small"
              checked={isHierarchyMoveEnabled}
              onChange={onHierarchyMoveEnabledChange}
              disabled={!canManage}
            />
          </div>
          {rootLocation && (rootLocation.children?.length ?? 0) === 0 && (
            <div style={{ marginTop: 12, color: token.colorTextSecondary, fontSize: 12 }}>
              まだエリアがありません（右の「＋エリア追加」から追加できます）
            </div>
          )}
        </>
      )}
    </Card>
  );
}
